/**
 * The Stock Overview page contract.
 *
 * One request feeds the whole screen: `GET /api/v1/stock/page`. The web app renders these
 * shapes and computes nothing that is not already here, so the number on the tile and the
 * number in the row come from one derivation in Postgres.
 *
 * Every timestamp in `as_of` comes from a fact table. Nothing on this page is allowed to
 * stamp the current clock and call it the age of the data.
 */
import { z } from 'zod';
import { CentsSchema, IsoInstantSchema, NyDateSchema, ProductFamilySchema, StockStatusSchema, } from './types.js';
/** How the 30 day window is cut. Owner tunable, one setting for the whole console. */
export const WindowModeSchema = z.enum(['rolling', 'calendar']);
export const CostSourceSchema = z.enum(['wac', 'opening', 'estimate', 'manual', 'none']);
/** One physical size under a family. The unit of the table body. */
export const StockPageRowSchema = z.object({
    product_id: z.number().int(),
    parent_id: z.number().int().nullable(),
    sku: z.string().nullable(),
    name: z.string(),
    size_label: z.string().nullable(),
    family: ProductFamilySchema,
    is_visible: z.boolean(),
    is_bundle: z.boolean(),
    /** Woo available to sell. The number the inline edit compares and sets. */
    on_hand: z.number().int(),
    committed_qty: z.number().int(),
    inbound_qty: z.number().int(),
    inbound_eta: NyDateSchema.nullable(),
    units_30d: z.number().int(),
    revenue_30d_cents: CentsSchema,
    velocity_per_day: z.number(),
    /** (on_hand + inbound) / velocity. Null means no paid sales in the window. */
    days_left: z.number().int().nullable(),
    status: StockStatusSchema,
    price_cents: CentsSchema,
    regular_price_cents: CentsSchema,
    sale_price_cents: CentsSchema.nullable(),
    /** on_hand x price x inv_value_factor. */
    inv_value_cents: CentsSchema,
    /** on_hand x WAC. */
    inventory_at_cost_cents: CentsSchema,
    inventory_at_retail_cents: CentsSchema,
    wac_cents: CentsSchema.nullable(),
    wac_source: CostSourceSchema,
    margin_pct: z.number().nullable(),
    best_price_cents: CentsSchema.nullable(),
    best_price_supplier: z.string().nullable(),
    best_price_negotiated: z.boolean(),
    quote_count: z.number().int(),
    /** Woo date_modified_gmt, echoed back as expected_version on a price edit. */
    woo_updated_at: IsoInstantSchema.nullable(),
});
/**
 * The money on a size row, named once. CONTRACTS.md 3.6: a packer never receives prices,
 * cost, WAC, margin, supplier price or any finance metric, and what is removed is absent
 * rather than null. `apps/api/src/modules/stock/domain/packer.ts` drops exactly these keys
 * and the web app renders nothing where they are missing.
 */
export const STOCK_PAGE_ROW_MONEY_KEYS = {
    revenue_30d_cents: true,
    price_cents: true,
    regular_price_cents: true,
    sale_price_cents: true,
    inv_value_cents: true,
    inventory_at_cost_cents: true,
    inventory_at_retail_cents: true,
    wac_cents: true,
    wac_source: true,
    margin_pct: true,
    best_price_cents: true,
    best_price_supplier: true,
    best_price_negotiated: true,
    quote_count: true,
};
/** A packer's size row: quantities, thresholds, days left and ETA. No money. */
export const StockPageRowPackerSchema = StockPageRowSchema.omit(STOCK_PAGE_ROW_MONEY_KEYS);
/** A family header row. Totals are sums over the sizes below it. */
export const StockPageFamilySchema = z.object({
    key: ProductFamilySchema,
    label: z.string(),
    on_hand: z.number().int(),
    units_30d: z.number().int(),
    revenue_30d_cents: CentsSchema,
    inv_value_cents: CentsSchema,
    inventory_at_cost_cents: CentsSchema,
    /** The most urgent size in the family. Null when nothing in it has sales. */
    worst_days_left: z.number().int().nullable(),
    critical: z.number().int(),
    low: z.number().int(),
    out: z.number().int(),
    rows: z.array(StockPageRowSchema),
});
export const STOCK_PAGE_FAMILY_MONEY_KEYS = {
    revenue_30d_cents: true,
    inv_value_cents: true,
    inventory_at_cost_cents: true,
};
export const StockPageFamilyPackerSchema = StockPageFamilySchema.omit(STOCK_PAGE_FAMILY_MONEY_KEYS).extend({ rows: z.array(StockPageRowPackerSchema) });
/** Thirty daily points, oldest first, aligned to the window. */
export const StockPageSparklinesSchema = z.object({
    units: z.array(z.number()),
    revenue_cents: z.array(z.number()),
    dates: z.array(NyDateSchema),
});
export const StockPageSparklinesPackerSchema = StockPageSparklinesSchema.omit({
    revenue_cents: true,
});
export const StockPageKpisSchema = z.object({
    critical: z.number().int(),
    low: z.number().int(),
    out: z.number().int(),
    active_skus: z.number().int(),
    hidden_skus: z.number().int(),
    /** 30 day revenue of SKUs whose days_left is under 14. */
    revenue_at_risk_cents: CentsSchema,
    revenue_at_risk_skus: z.number().int(),
    inventory_at_cost_cents: CentsSchema,
    inventory_at_retail_cents: CentsSchema,
    inv_value_cents: CentsSchema,
    open_po_value_cents: CentsSchema,
    open_po_count: z.number().int(),
    next_po_eta: NyDateSchema.nullable(),
    units_30d: z.number().int(),
    units_prior_30d: z.number().int(),
    revenue_30d_cents: CentsSchema,
    revenue_prior_30d_cents: CentsSchema,
    /** Catalog margin: (revenue - cogs) / revenue over the window. Null when revenue is zero. */
    margin_pct: z.number().nullable(),
    margin_prior_pct: z.number().nullable(),
    sparklines: StockPageSparklinesSchema,
});
/**
 * Every finance tile. `revenue_at_risk_skus` goes with its tile: it is a count, but a count
 * defined by revenue, and CONTRACTS.md 3.6 says "any finance metric".
 */
export const STOCK_PAGE_KPI_MONEY_KEYS = {
    revenue_at_risk_cents: true,
    revenue_at_risk_skus: true,
    inventory_at_cost_cents: true,
    inventory_at_retail_cents: true,
    inv_value_cents: true,
    open_po_value_cents: true,
    revenue_30d_cents: true,
    revenue_prior_30d_cents: true,
    margin_pct: true,
    margin_prior_pct: true,
};
export const StockPageKpisPackerSchema = StockPageKpisSchema.omit(STOCK_PAGE_KPI_MONEY_KEYS).extend({
    sparklines: StockPageSparklinesPackerSchema,
});
/**
 * Real ages, each from the table that produced the number. A null means the table is empty,
 * which the UI renders as "never", never as "just now".
 */
export const StockPageAsOfSchema = z.object({
    products: IsoInstantSchema.nullable(),
    orders: IsoInstantSchema.nullable(),
    wac: IsoInstantSchema.nullable(),
    supplier_prices: IsoInstantSchema.nullable(),
    purchase_orders: IsoInstantSchema.nullable(),
    legacy_import: IsoInstantSchema.nullable(),
});
export const StockPageWindowSchema = z.object({
    mode: WindowModeSchema,
    start: IsoInstantSchema,
    end: IsoInstantSchema,
    prior_start: IsoInstantSchema,
    prior_end: IsoInstantSchema,
    days: z.number().int(),
    label: z.string(),
});
export const StockPageConfigSchema = z.object({
    inv_value_factor: z.number(),
    critical_days: z.number().int(),
    low_days: z.number().int(),
    at_risk_days: z.number().int(),
    /** Setting keys still waiting on an owner decision. The legend marks these. */
    pending_todd: z.array(z.string()),
});
export const StockPageResponseSchema = z.object({
    kpis: StockPageKpisSchema,
    families: z.array(StockPageFamilySchema),
    /** Hidden SKUs, out of every KPI and out of the families, listed at the foot of the page. */
    hidden: z.array(StockPageRowSchema),
    as_of: StockPageAsOfSchema,
    window: StockPageWindowSchema,
    config: StockPageConfigSchema,
    /** Server time spent building this response, milliseconds. */
    took_ms: z.number(),
});
/** The same screen for a packer: health counts, quantities, ETAs. Absent money, not null. */
export const StockPageResponsePackerSchema = StockPageResponseSchema.extend({
    kpis: StockPageKpisPackerSchema,
    families: z.array(StockPageFamilyPackerSchema),
    hidden: z.array(StockPageRowPackerSchema),
});
/* ------------------------------------------------------------------ */
/* Row detail                                                          */
/* ------------------------------------------------------------------ */
export const StockDetailPointSchema = z.object({
    date: NyDateSchema,
    units: z.number().int(),
    revenue_cents: CentsSchema,
});
export const StockPriceChangeSchema = z.object({
    at: IsoInstantSchema,
    old_cents: CentsSchema.nullable(),
    new_cents: CentsSchema,
    actor: z.string().nullable(),
});
export const StockOpenPoSchema = z.object({
    po_id: z.string(),
    po_number: z.string(),
    supplier_name: z.string().nullable(),
    status: z.string(),
    qty_ordered: z.number().int(),
    qty_received: z.number().int(),
    eta: NyDateSchema.nullable(),
});
export const StockQuoteSchema = z.object({
    supplier_price_id: z.string(),
    supplier_id: z.string(),
    supplier_name: z.string(),
    unit_price_cents: CentsSchema,
    negotiated_price_cents: CentsSchema.nullable(),
    effective_unit_cents: CentsSchema,
    negotiated: z.boolean(),
    moq: z.number().int().nullable(),
    valid_from: NyDateSchema.nullable(),
    valid_to: NyDateSchema.nullable(),
    updated_at: IsoInstantSchema.nullable(),
    /** Landed estimate when a freight rate for this supplier is known, else null. */
    landed_estimate_cents: CentsSchema.nullable(),
});
export const StockRowDetailSchema = z.object({
    product_id: z.number().int(),
    sku: z.string().nullable(),
    name: z.string(),
    size_label: z.string().nullable(),
    woo_updated_at: IsoInstantSchema.nullable(),
    series_90d: z.array(StockDetailPointSchema),
    units_30d: z.number().int(),
    units_prior_30d: z.number().int(),
    price_history: z.array(StockPriceChangeSchema),
    open_pos: z.array(StockOpenPoSchema),
    quotes: z.array(StockQuoteSchema),
    as_of: StockPageAsOfSchema,
});
export const StockDetailPointPackerSchema = StockDetailPointSchema.omit({ revenue_cents: true });
/** The drawer for a packer: units and open purchase orders. No price history, no quotes. */
export const StockRowDetailPackerSchema = StockRowDetailSchema.omit({
    price_history: true,
    quotes: true,
}).extend({ series_90d: z.array(StockDetailPointPackerSchema) });
/* ------------------------------------------------------------------ */
/* Writes                                                              */
/* ------------------------------------------------------------------ */
/**
 * Inline stock edit. Compare and set: `expected_qty` is what the operator saw. The bridge
 * rejects with 409 when Woo has moved, and the UI shows the live number rather than
 * overwriting someone else's count.
 */
export const StockSetQtyRequestSchema = z.object({
    expected_qty: z.number().int(),
    qty: z.number().int().min(0).max(1_000_000),
});
export const StockSetQtyResponseSchema = z.object({
    product_id: z.number().int(),
    qty_before: z.number().int(),
    qty_after: z.number().int(),
    adjustment_id: z.string(),
});
/** Inline price edit. No reason, no step up: one owner, and the audit row carries the diff. */
export const StockSetPriceInlineRequestSchema = z.object({
    expected_version: z.string().min(1),
    price_cents: z.number().int().min(0).max(100_000_00),
    reason: z.string().max(500).nullish(),
});
export const StockSetVisibleRequestSchema = z.object({
    is_visible: z.boolean(),
    note: z.string().max(500).nullish(),
});
export const StockSetVisibleResponseSchema = z.object({
    product_id: z.number().int(),
    is_visible: z.boolean(),
    /** True when the WordPress postmeta write succeeded. False means ops and legacy disagree. */
    wp_synced: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* Legend                                                              */
/* ------------------------------------------------------------------ */
export const LegendEntrySchema = z.object({
    id: z.string(),
    label: z.string(),
    definition: z.string(),
    version: z.number().int(),
    unit: z.string(),
    basis: z.string(),
    sources: z.array(z.string()),
    /** Set when the definition is a working default the owner has not confirmed. */
    pending_owner: z.boolean(),
    never_confuse_with: z.string().nullable(),
});
export const LegendResponseSchema = z.object({
    screen: z.string(),
    entries: z.array(LegendEntrySchema),
    config: StockPageConfigSchema,
    window: StockPageWindowSchema,
});
/* ------------------------------------------------------------------ */
/* WAC breakdown                                                       */
/* ------------------------------------------------------------------ */
/**
 * How one WAC was built, batch by batch.
 *
 * The overview shows a single cost per size and nothing about where it came from, which is
 * fine until a number looks wrong. This is the audit trail behind that one cell: the batches
 * that feed the average, the ones that do not and why, and the division itself with the real
 * numbers in it.
 *
 * `wac_cents` is whatever `domain/wacCurrent.ts` says it is and nothing here re-derives it.
 * `computed_wac_cents` is the batch average alone, which is the same number except when a
 * manual override is in force; showing both is the only way an override is visible rather
 * than merely effective.
 */
export const StockWacAllocationsSchema = z.object({
    freight_cents: CentsSchema,
    duties_cents: CentsSchema,
    testing_cents: CentsSchema,
    labeling_cents: CentsSchema,
    payment_fees_cents: CentsSchema,
    other_cents: CentsSchema,
    /** Per unit sum of the six above. Added to unit goods cost it is the landed unit cost. */
    total_cents: CentsSchema,
});
export const StockWacBatchSchema = z.object({
    batch_id: z.string(),
    lot_number: z.string().nullable(),
    supplier_name: z.string().nullable(),
    received_date: NyDateSchema.nullable(),
    qty_received: z.number().int(),
    qty_remaining: z.number().int(),
    unit_goods_cents: CentsSchema,
    allocations: StockWacAllocationsSchema,
    landed_unit_cents: CentsSchema,
    /** qty_received x landed_unit_cents. The numerator's contribution from this batch. */
    line_landed_cents: CentsSchema,
    cost_status: z.enum(['provisional', 'final']),
    status: z.string(),
    /**
     * A synthetic receipt the cutover import created so no SKU was costless on day one. Some
     * carry no cost at all, which is why a low WAC on one of these is not a bug.
     */
    is_opening_balance: z.boolean(),
});
/** A batch the average leaves out, with the reason stated rather than implied. */
export const StockWacExcludedBatchSchema = z.object({
    batch_id: z.string(),
    lot_number: z.string().nullable(),
    supplier_name: z.string().nullable(),
    received_date: NyDateSchema.nullable(),
    qty_received: z.number().int(),
    landed_unit_cents: CentsSchema,
    status: z.string(),
    reason: z.string(),
});
export const StockWacBreakdownSchema = z.object({
    product_id: z.number().int(),
    sku: z.string().nullable(),
    name: z.string(),
    size_label: z.string().nullable(),
    /** The number the overview row shows, from the one WAC function. */
    wac_cents: CentsSchema.nullable(),
    wac_source: CostSourceSchema,
    /** The definition in words, so the drawer does not need the reader to know the code. */
    formula: z.string(),
    /** Set when an owner entered a cost by hand. It wins over the batch average outright. */
    override_cents: CentsSchema.nullable(),
    /** When the override was recorded. `wac_ledger.created_at`, not its date-only event_date. */
    override_at: IsoInstantSchema.nullable(),
    /** The batch average on its own. Null when no batch feeds it. */
    computed_wac_cents: CentsSchema.nullable(),
    batches: z.array(StockWacBatchSchema),
    excluded: z.array(StockWacExcludedBatchSchema),
    /** The footer's arithmetic: total_landed_cents / total_qty_received = computed_wac_cents. */
    total_landed_cents: CentsSchema,
    total_qty_received: z.number().int(),
    opening_balance_count: z.number().int(),
});
/**
 * The breakdown for a packer is the batch list alone: which lots fed the shelf, when and how
 * many. Every cost, the formula and the footer arithmetic are absent.
 */
export const StockWacBatchPackerSchema = StockWacBatchSchema.pick({
    batch_id: true,
    lot_number: true,
    supplier_name: true,
    received_date: true,
    qty_received: true,
    qty_remaining: true,
    status: true,
    is_opening_balance: true,
});
export const StockWacExcludedBatchPackerSchema = StockWacExcludedBatchSchema.pick({
    batch_id: true,
    lot_number: true,
    supplier_name: true,
    received_date: true,
    qty_received: true,
    status: true,
    reason: true,
});
export const StockWacBreakdownPackerSchema = StockWacBreakdownSchema.pick({
    product_id: true,
    sku: true,
    name: true,
    size_label: true,
    total_qty_received: true,
    opening_balance_count: true,
}).extend({
    batches: z.array(StockWacBatchPackerSchema),
    excluded: z.array(StockWacExcludedBatchPackerSchema),
});
//# sourceMappingURL=stockPage.js.map